import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Archive, Eye, Search } from "lucide-react";
import SLABadge from "../components/SLABadge";
import api from "../lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
  COMPLETED: "bg-slate-50 text-slate-600 border-slate-200",
  CANCELLED: "bg-red-50 text-red-700 border-red-200",
};

function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
}

export default function ArchivePage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);

  useEffect(() => { loadArchived(); }, []);

  const loadArchived = async () => {
    try {
      const { data } = await api.get("/job-cards/archived/list");
      setJobs(data.items || []);
      setTotal(data.total || 0);
    } catch { toast.error("Failed to load archive"); }
    setLoading(false);
  };

  const filtered = search
    ? jobs.filter(j =>
        j.job_card_number?.toLowerCase().includes(search.toLowerCase()) ||
        j.model?.toLowerCase().includes(search.toLowerCase()) ||
        j.store?.toLowerCase().includes(search.toLowerCase())
      )
    : jobs;

  return (
    <div data-testid="archive-page" className="space-y-6">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900 flex items-center gap-2">
          <Archive size={24} className="text-teal-600" /> Archived Job Cards
        </h1>
        <p className="text-slate-500 text-sm mt-1">{total} archived records</p>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="archive-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search archived JCs..."
          className="bg-white border-slate-200 pl-9 text-sm"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
              <th className="text-left p-3">Job Card #</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Model</th>
              <th className="text-left p-3">Store</th>
              <th className="text-left p-3">Area</th>
              <th className="text-left p-3">Created</th>
              <th className="text-left p-3">Archived</th>
              <th className="text-right p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="p-8 text-center text-slate-400">Loading...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="p-12 text-center">
                  <Archive size={32} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-400">No archived job cards</p>
                </td>
              </tr>
            )}
            {!loading && filtered.map((jc) => (
              <tr key={jc.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="p-3 font-medium text-slate-800">{jc.job_card_number}</td>
                <td className="p-3">
                  <Badge variant="outline" className={`status-badge ${STATUS_COLORS[jc.status] || ""}`}>
                    {jc.status}
                  </Badge>
                </td>
                <td className="p-3 text-slate-600">{jc.model || "-"}</td>
                <td className="p-3 text-slate-600">{jc.store || "-"}</td>
                <td className="p-3 text-slate-600">{jc.area || "-"}</td>
                <td className="p-3 text-slate-400 text-xs">{formatDate(jc.date_created)}</td>
                <td className="p-3 text-slate-400 text-xs">{formatDate(jc.date_archived)}</td>
                <td className="p-3 text-right">
                  <Button
                    data-testid={`view-archived-${jc.id}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/jobs/${jc.id}`)}
                    className="text-teal-600 hover:text-teal-700 text-xs"
                  >
                    <Eye size={14} className="mr-1" /> View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
